import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import maintenanceAssignmentsService from "./maintenanceAssignments.service";
import {
  MaintenanceAssignment,
  MaintenanceAssignmentFilters,
  MaintenanceAssignmentPagination,
  MaintenanceRole,
} from "./types";

const defaultPagination: MaintenanceAssignmentPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
};

const getRequestErrorMessage = (error: unknown) => {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return (
      error.response?.data?.message ||
      "Unable to load Spare Parts role assignments. Please retry."
    );
  }

  return error instanceof Error
    ? error.message
    : "Unable to load Spare Parts role assignments. Please retry.";
};

export const useMaintenanceAssignments = () => {
  const [items, setItems] = useState<MaintenanceAssignment[]>([]);
  const [pagination, setPagination] =
    useState<MaintenanceAssignmentPagination>(defaultPagination);
  const [filters, setFilters] = useState<MaintenanceAssignmentFilters>({
    page: 1,
    limit: 20,
    search: "",
    hubId: "",
    departmentId: "",
    maintenanceRole: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await maintenanceAssignmentsService.getAssignments(filters);
      setItems(response.items);
      setPagination(response.pagination || defaultPagination);
      setError(null);
    } catch (requestError) {
      // Keep the last successful list visible while a refresh is unavailable.
      console.error("Unable to load Spare Parts role assignments.", requestError);
      setError(getRequestErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const updateFilters = (next: Partial<MaintenanceAssignmentFilters>) => {
    setFilters((current) => ({
      ...current,
      ...next,
    }));
  };

  const assignRole = async (
    employeeId: string,
    maintenanceRole: MaintenanceRole | null,
    cityId?: string | null
  ) => {
    try {
      await maintenanceAssignmentsService.changeRole(employeeId, {
        maintenanceRole,
        cityId,
        assignedBy: "ADMIN",
      });
      await fetchAssignments();
    } catch (requestError) {
      console.error("Unable to update Spare Parts role assignment.", requestError);
      setError(getRequestErrorMessage(requestError));
    }
  };

  return {
    items,
    pagination,
    filters,
    loading,
    error,
    refresh: fetchAssignments,
    updateFilters,
    assignRole,
  };
};

export default useMaintenanceAssignments;
